/**
 * タイピング開始確認で受け取る失敗を、SDK に依存せず画面用のことばへ変換する。
 *
 * `code` / `details.reason` は callable と同じ形だが、ここは Firebase を import
 * しない。テストやローカル実装の通常の Error も安全に扱う。
 */

export type TypingFailureKind = 'reload' | 'auth' | 'stage-info' | 'retry';

export interface TypingFailure {
  readonly kind: TypingFailureKind;
  readonly message: string;
}

const RETRY_MESSAGE = 'ステージを じゅんびできなかったよ。もういちど ためしてね';

function stringField(value: unknown, field: string): string | null {
  if (typeof value !== 'object' || value === null || !(field in value)) return null;
  const candidate = (value as Record<string, unknown>)[field];
  return typeof candidate === 'string' ? candidate : null;
}

function errorReason(error: unknown): string | null {
  const details =
    typeof error === 'object' && error !== null ? (error as Record<string, unknown>).details : null;
  return stringField(details, 'reason');
}

/** SDK 風の `code` / `details.reason` を小学生向けの開始失敗案内へ分類する。 */
export function classifyTypingFailure(error: unknown): TypingFailure {
  const code = stringField(error, 'code') ?? 'unknown';
  const reason = errorReason(error);

  if (code === 'recovery-required' || reason === 'recovery-required') {
    return { kind: 'reload', message: 'ページを よみこみなおしてね' };
  }
  if (
    code === 'functions/unauthenticated' ||
    code === 'unauthenticated' ||
    code === 'functions/permission-denied' ||
    code === 'permission-denied'
  ) {
    return {
      kind: 'auth',
      message: 'ログインや せつぞくの かくにんが できなかったよ。ページを よみこみなおしてね',
    };
  }
  if (
    code === 'functions/invalid-argument' ||
    code === 'invalid-argument' ||
    code === 'functions/not-found' ||
    code === 'not-found'
  ) {
    return {
      kind: 'stage-info',
      message: 'ステージの じょうほうを かくにんできませんでした。ページを よみこみなおしてね',
    };
  }
  return { kind: 'retry', message: RETRY_MESSAGE };
}
